import React, { FC, RefObject, useCallback, useEffect, useMemo } from "react";
import { LayoutChangeEvent, NativeScrollEvent, NativeSyntheticEvent, Platform, ScrollView } from "react-native";
import {
  Box,
  View,
  useDevice,
  Spinner,
  SPACE,
  ALIGN,
  Banner,
  FLEX_DIRECTION,
  BANNER_TYPE,
  Layout as AuroraLayout,
} from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Text } from "@lookiero/sty-psp-ui";
import { ProductPreview } from "../../../../../domain/projections/ProductPreview";
import { TrendingForYouLook } from "../../../../../domain/projections/TrendingForYouLook";
import { BackToTopButton } from "../../../components/backToTopButton/BackToTopButton";
import { CrossSellingBanner } from "../../../components/crossSellingBanner/CrossSellingBanner";
import { IconMessage } from "../../../components/iconMessage/IconMessage";
import { LookList } from "../../../components/lookList/LookList";
import { UpdateYourProfileBanner } from "../../../components/updateYourProfileBanner/UpdateYourProfileBanner";
import { useCheckIfElementsAreInView } from "../../../hooks/useInView";
import { useStaticInfo } from "../../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages } from "../../../i18n/i18n";
import { OS } from "../../../shared/config";
import { OfferingContent } from "../offeringLayout/OfferingContent";
import { style as trendingForYouStyle } from "./TrendingForYou.style";

interface TrendingForYouProps {
  readonly isLoading: boolean;
  readonly looks: TrendingForYouLook[] | undefined;
  readonly scrollViewRef: RefObject<ScrollView | null>;
  readonly onCrossNavigation: (buttonId: string) => void;
  readonly onPressLook: ({ lookId, product }: { lookId: string; product: ProductPreview }) => void;
  readonly onScroll: (coords: { X: number; Y: number }) => void;
  readonly onLayoutLooks: (event: LayoutChangeEvent) => void;
  readonly onBackToStyleProfile: () => void;
  readonly onBack: () => void;
}

const TrendingForYou: FC<TrendingForYouProps> = ({
  looks,
  isLoading,
  scrollViewRef,
  onCrossNavigation,
  onPressLook,
  onScroll,
  onLayoutLooks,
  onBackToStyleProfile,
  onBack,
}) => {
  const { screen } = useDevice();
  const isDesktop = screen.L;

  const trendingForYouTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.TRENDING_FOR_YOU_TITLE,
  });
  const trendingForYouSubtitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.TRENDING_FOR_YOU_SUBTITLE,
  });
  const trendingForYouBottomTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.TRENDING_FOR_YOU_BOTTOM_TITLE,
  });
  const trendingForYouBottomSubtitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.TRENDING_FOR_YOU_BOTTOM_SUBTITLE,
  });
  const trendingForYouCrossNavigationTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.TRENDING_FOR_YOU_CROSS_NAVIGATION_TITLE,
  });
  const trendingForYouCrossNavigationText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.TRENDING_FOR_YOU_CROSS_NAVIGATION_TEXT,
  });
  const trendingForYouCrossNavigationCtaText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.TRENDING_FOR_YOU_CROSS_NAVIGATION_CTA,
  });

  const style = useMemo(() => trendingForYouStyle(), []);

  const {
    cdnPath,
    session: {
      user: { segment },
    },
  } = useStaticInfo();
  const imageBannerUrl = useMemo(
    () => `${cdnPath}/images/shop/banners/complete-your-look/${segment}.webp`,
    [cdnPath, segment],
  );

  const hasLooks = Boolean(looks?.length);

  const checkIfElementsAreInView = useCheckIfElementsAreInView();
  const handleOnScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      onScroll({ X: event.nativeEvent.contentOffset.x, Y: event.nativeEvent.contentOffset.y });

      checkIfElementsAreInView();
    },
    [onScroll, checkIfElementsAreInView],
  );

  // Hack to fix that address bar issue in mobile browsers
  // https://developer.chrome.com/blog/url-bar-resizing/
  // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/

  const onResize = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
    document.body.setAttribute("style", `height: calc(var(--vh, 1vh) * 100);`);
  };

  useEffect(() => {
    if (Platform.OS === OS.WEB) {
      window.addEventListener("resize", onResize);
    }
    return () => {
      if (Platform.OS === "web") {
        window.removeEventListener("resize", onResize);
      }
    };
  }, []);

  return (
    <OfferingContent
      scrollRef={scrollViewRef}
      style={{ scrollView: style.mainContentContainer, footer: style.footer }}
      footer={
        screen.S && looks?.length ? (
          <BackToTopButton scrollViewRef={scrollViewRef} section="shop_trending_for_you" />
        ) : undefined
      }
      onScroll={handleOnScroll}
    >
      {isLoading ? (
        <View style={[style.container, style.centeredContainer]}>
          <Spinner />
        </View>
      ) : (
        <View style={style.container}>
          {looks?.length ? (
            <>
              <View style={[style.titleWrapper, isDesktop ? style.titleWrapperDesktop : style.titleWrapperMobile]}>
                <Text
                  level={isDesktop ? 3 : 1}
                  style={isDesktop ? style.titleDesktop : style.titleMobile}
                  testID="home-title"
                  variant={isDesktop ? "heading" : "body"}
                >
                  {trendingForYouTitleText}
                </Text>

                <Text
                  level={isDesktop ? 3 : 2}
                  style={[style.subtitle, isDesktop ? style.subtitleDesktop : undefined]}
                  variant={isDesktop ? "body" : "detail"}
                >
                  {trendingForYouSubtitleText}
                </Text>
              </View>

              <View style={[style.looksWrapper, isDesktop ? style.looksWrapperDesktop : style.looksWrapperMobile]}>
                <LookList looks={looks} onLayoutLooks={onLayoutLooks} onPressLook={onPressLook} />
              </View>
            </>
          ) : (
            <UpdateYourProfileBanner onBackToStyleProfile={onBackToStyleProfile} />
          )}
          <AuroraLayout>
            <Box align={ALIGN.CENTER} paddingTop={!hasLooks ? SPACE.SPACE_16 : undefined} size={{ L: "2/3" }}>
              {hasLooks ? (
                <IconMessage
                  body={trendingForYouBottomSubtitleText}
                  style={style.bottomMessage}
                  title={trendingForYouBottomTitleText}
                />
              ) : null}
              <View
                flexDirection={isDesktop ? FLEX_DIRECTION.ROW : FLEX_DIRECTION.COLUMN}
                marginBottom={isDesktop ? SPACE.SPACE_20 : SPACE.SPACE_8}
              >
                <Banner
                  description={trendingForYouCrossNavigationText}
                  style={{ flex: 1 }}
                  title={trendingForYouCrossNavigationTitleText}
                  type={BANNER_TYPE.ROUNDED}
                  image={{
                    src: imageBannerUrl,
                  }}
                  primaryButton={{
                    text: trendingForYouCrossNavigationCtaText,
                    onPress: () => onCrossNavigation("cross-navigation-button"),
                  }}
                />
                <View margin={isDesktop ? SPACE.SPACE_3 : SPACE.SPACE_2} />
                <CrossSellingBanner style={style.crossSellingBanner} onPress={onBack} />
              </View>
            </Box>
          </AuroraLayout>
        </View>
      )}
    </OfferingContent>
  );
};

export type { TrendingForYouProps };
export { TrendingForYou };
